import useScrollReveal from '../../hooks/useScrollReveal';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from '../../components/feature/HeroSection';
import TrustBar from '../../components/feature/TrustBar';
import AIAnswerBlock from '../../components/feature/AIAnswerBlock';
import MidCTABanner from '../../components/feature/MidCTABanner';
import ProcessSection from '../../components/feature/ProcessSection';
import WhyChooseUs from '../../components/feature/WhyChooseUs';
import ServiceAreasSection from '../../components/feature/ServiceAreasSection';
import TestimonialsSection from '../../components/feature/TestimonialsSection';
import FAQSection from '../../components/feature/FAQSection';
import FinalCTA from '../../components/feature/FinalCTA';

const galleryImages = [
  {
    src: 'https://public.readdy.ai/ai/img_res/26a1a7becbf348f715f737020db0101a.jpg',
    alt: 'Masters Fencing wood fence project St. Mary\'s GA',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Elegant%20ornamental%20black%20steel%20fence%20at%20upscale%20home%20entrance%2C%20beautiful%20powder-coated%20iron%20with%20decorative%20spear%20tops%2C%20manicured%20green%20lawn%2C%20golden%20afternoon%20light%2C%20Georgia%20residential%20luxury%20property&width=600&height=520&seq=mf_v3_gal2&orientation=portrait',
    alt: 'Masters Fencing steel fence project Camden County',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Crisp%20clean%20white%20vinyl%20privacy%20fence%20in%20bright%20upscale%20backyard%2C%20smooth%20panels%2C%20perfectly%20aligned%2C%20green%20grass%20and%20flowering%20plants%20in%20foreground%2C%20sunny%20residential%20Georgia%20property%20photography&width=600&height=520&seq=mf_v3_gal3&orientation=portrait',
    alt: 'Masters Fencing vinyl fence project',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Custom%20dark%20bronze%20aluminum%20architectural%20fence%20surrounding%20luxury%20pool%2C%20elegant%20bar%20design%2C%20sparkling%20blue%20water%2C%20resort%20landscaping%2C%20premium%20Southern%20residential%20estate%2C%20afternoon%20ambient%20light%20photography&width=600&height=520&seq=mf_v3_gal4&orientation=portrait',
    alt: 'Masters Fencing aluminum fence pool enclosure',
  },
];

const values = [
  {
    icon: 'fa-award',
    title: 'Quality First',
    desc: "We never cut corners on materials or workmanship. Every fence is built to endure Georgia's climate and the genuine test of time.",
  },
  {
    icon: 'fa-handshake',
    title: 'Honest Service',
    desc: 'Transparent pricing, clear timelines, zero hidden fees. We treat every client exactly the way we want to be treated.',
  },
  {
    icon: 'fa-map-location-dot',
    title: 'Local Roots',
    desc: "Proud members of the St. Mary's community for over 20 years. Our reputation is built locally — we stand behind every project we touch.",
  },
  {
    icon: 'fa-users',
    title: 'Client-Centered',
    desc: "From first consultation to final walkthrough, your satisfaction drives every decision. We listen, we advise, and we deliver precisely what you envisioned.",
  },
];

const AboutPage: React.FC = () => {
  useScrollReveal();

  return (
    <div className="bg-[#FAF8F5]">
      <Navbar />
      <main>
        <HeroSection
          label="About Masters Fencing · St. Mary's, GA"
          headline="Two Decades of Craftsmanship, Community & Trust"
          subheadline="Locally owned and operated since the early 2000s, Masters Fencing has built its reputation one fence at a time — through precision work, genuine reliability, and deep care for the communities we serve."
          bgImage="https://readdy.ai/api/search-image?query=Skilled%20master%20craftsman%20fencing%20contractor%20working%20alone%20at%20golden%20hour%20carefully%20installing%20premium%20cedar%20fence%20boards%20by%20hand%2C%20warm%20orange%20and%20gold%20light%20illuminating%20workspace%2C%20sawdust%20in%20the%20air%2C%20authentic%20real%20work%20moment%2C%20lush%20green%20Southern%20backyard%2C%20professional%20documentary%20photography%2C%20inspiring%20craftsmanship%2C%20authentic%20hard%20work&width=1920&height=1080&seq=mf_v3_hero_about&orientation=landscape"
          primaryCTA="Request a Consultation"
        />
        <TrustBar />

        {/* Story section */}
        <section className="relative bg-[#FAF8F5]">
          <div className="section-py max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

              {/* Left — copy */}
              <div className="reveal-left">
                <div className="flex items-center gap-3 mb-6">
                  <span className="gold-bar" />
                  <span className="text-[#C4A84F] text-[11px] tracking-[0.38em] uppercase font-sans font-medium">
                    Our Story
                  </span>
                </div>
                <h2 className="font-serif text-[3.2rem] md:text-[4.2rem] text-[#0D0C0B] leading-[0.95] mb-8">
                  Built on Honesty,<br />Craftsmanship &amp;<br />Local Trust
                </h2>
                <div className="w-16 h-[1px] bg-[#C4A84F] mb-8" />
                <div className="space-y-5 text-[#0D0C0B]/55 text-base leading-relaxed font-sans">
                  <p>
                    Masters Fencing was founded on a simple but unwavering mission: deliver fencing that lasts, at a price that is fair, with service that genuinely respects every customer. Over two decades later, that mission still guides every project we accept across St. Mary's and Camden County.
                  </p>
                  <p>
                    Led by Josh Masters, our team brings hands-on expertise to every installation — whether it is a 50-foot residential privacy fence or a 400-foot commercial perimeter. We work with premium materials including cedar, chain-link, steel, vinyl, and aluminum to create solutions precisely tailored to each client's needs.
                  </p>
                  <p>
                    We believe a fence is more than a boundary — it is an investment in your safety, your privacy, and your property's long-term value. From the first consultation to the final walkthrough, we prioritize clear communication, on-time delivery, and results that consistently exceed expectations.
                  </p>
                </div>

                <div className="mt-10 grid grid-cols-2 gap-3">
                  {[
                    { value: '20+', label: 'Years in Business' },
                    { value: '500+', label: 'Projects Completed' },
                    { value: '5 ★', label: 'Google Rating' },
                    { value: '9', label: 'Cities Served' },
                  ].map((s) => (
                    <div key={s.label} className="border border-[#0D0C0B]/8 p-5 bg-white">
                      <div className="font-serif text-[2.4rem] text-[#C4A84F] leading-none mb-1">{s.value}</div>
                      <div className="text-[9px] text-[#0D0C0B]/40 tracking-[0.22em] uppercase font-sans">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — gallery grid */}
              <div className="reveal-right grid grid-cols-2 gap-3">
                {galleryImages.map((img, i) => (
                  <div
                    key={i}
                    className={`overflow-hidden img-zoom ${i % 2 === 1 ? 'mt-8' : ''}`}
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full h-60 object-cover object-center"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Values section */}
        <section
          className="relative overflow-hidden"
          style={{ background: 'linear-gradient(150deg, #0A0908 0%, #1A1510 100%)' }}
        >
          <div className="gold-separator" />
          <div className="section-py max-w-7xl mx-auto px-6">
            <div className="text-center mb-16 reveal">
              <div className="flex items-center justify-center gap-3 mb-6">
                <span className="gold-bar" />
                <span className="text-[#C4A84F] text-[11px] tracking-[0.38em] uppercase font-sans font-medium">
                  Our Values
                </span>
                <span className="gold-bar" />
              </div>
              <h2 className="font-serif text-[3.2rem] md:text-[4.2rem] text-[#F5F1E8] leading-[0.95]">
                What Drives Us Every Day
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="reveal bg-white/[0.04] border border-white/8 p-8 text-center hover:border-[#C4A84F]/38 hover:bg-white/[0.06] transition-all duration-400 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-6 border border-[#C4A84F]/28 group-hover:bg-[#C4A84F] group-hover:border-[#C4A84F] transition-all duration-300">
                    <i className={`fa-solid ${v.icon} text-xl text-[#C4A84F] group-hover:text-[#0D0C0B] transition-colors`} />
                  </div>
                  <h3 className="font-serif text-[1.4rem] text-[#F5F1E8] mb-3">{v.title}</h3>
                  <p className="text-[#F5F1E8]/45 text-sm leading-relaxed font-sans">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="gold-separator" />
        </section>

        <MidCTABanner
          headline="Start Your Project With Confidence"
          sub="20+ years of craftsmanship. Free estimates. $100 off same-day signing. No pressure, just genuine expertise."
          cta="Request a Free Estimate"
        />
        <AIAnswerBlock
          focusTitle="A Legacy of Fencing Excellence"
          focusDescription="Masters Fencing has spent over 20 years perfecting the craft of fence installation in St. Mary's, Georgia. Our deep roots in Camden County, combined with a relentless commitment to premium materials and honest workmanship, have earned the trust of hundreds of local homeowners and businesses."
        />
        <ProcessSection />
        <WhyChooseUs />
        <ServiceAreasSection />
        <TestimonialsSection />
        <FAQSection />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
